import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { NavLink, useNavigate } from "react-router-dom";
import {
    HomeIcon,
    UserIcon,
    UserGroupIcon,
    TruckIcon,
    MapPinIcon,
    WalletIcon,
    ShieldExclamationIcon,
    SignalIcon,
    BellIcon,
    ChartBarIcon,
    UserCircleIcon,
    ClipboardDocumentListIcon,
    ClipboardDocumentCheckIcon,
    Cog6ToothIcon,
} from "@heroicons/react/24/outline";

import { primaryNavigation } from "@/config/navigation";
import { NAVIGATION_PERMISSION_RULES } from "@/config/permissions";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/features/auth/state/useAuth";
import { cn } from "@/lib/cn";

import logoUnnamed from "@/assets/applogo-unamed.svg";

const navIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    "/": HomeIcon,
    "/users": UserIcon,
    "/riders": UserGroupIcon,
    "/drivers": TruckIcon,
    "/ride-bookings-trips": MapPinIcon,
    "/wallet-payments": WalletIcon,
    "/fraud-safety": ShieldExclamationIcon,
    "/live-monitoring": SignalIcon,
    "/notifications": BellIcon,
    "/approval-queue": ClipboardDocumentCheckIcon,
    "/reports-analytics": ChartBarIcon,
    "/admin-management": UserCircleIcon,
    "/platform-configuration": Cog6ToothIcon,
    "/audit-logs": ClipboardDocumentListIcon,
    "/settings": Cog6ToothIcon,
};

export const Sidebar = () => {
    const { logout, user, hasAnyPermission, hasAllPermissions } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/auth/login");
    };

    const visibleNavigation = primaryNavigation.filter((item) => {
        const rule = NAVIGATION_PERMISSION_RULES[item.path];

        if (!rule) {
            return true;
        }

        const meetsRoleRequirement =
            !rule.roles || rule.roles.length === 0 || (user?.role ? rule.roles.includes(user.role) : false);

        const meetsAnyRequirement =
            !rule.anyOf || rule.anyOf.length === 0 || hasAnyPermission(rule.anyOf);

        const meetsAllRequirement =
            !rule.allOf || rule.allOf.length === 0 || hasAllPermissions(rule.allOf);

        return meetsRoleRequirement && meetsAnyRequirement && meetsAllRequirement;
    });

    return (
        <aside className="flex h-full w-64 flex-col border-r border-slate-200 bg-white/60 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/60">
            <div className="flex h-16 items-center gap-2 px-6">
                <img src={logoUnnamed} alt="RideToFive" className="h-10 w-10" />
                <div>
                    <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
                        RideToFive
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Admin Console</p>
                </div>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-4">
                {visibleNavigation.map((item, idx) => {
                    const Icon = navIcons[item.path];
                    return (
                        <div key={item.path}>
                            <NavLink
                                to={item.path}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-primary-50 hover:text-primary-600 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-primary-400",
                                        isActive && "bg-primary-100 text-primary-700 dark:bg-slate-800 dark:text-primary-300"
                                    )
                                }
                                end={item.path === "/"}
                            >
                                {Icon && <Icon className="h-5 w-5 shrink-0" />}
                                <span>{item.label}</span>
                            </NavLink>
                            {idx < visibleNavigation.length - 1 && (
                                <hr className="my-1 border-slate-200 dark:border-slate-800" />
                            )}
                        </div>
                    );
                })}
            </nav>
            <div className="px-4 pb-6">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleLogout}
                    className="w-full justify-start gap-3 border-rose-200 text-rose-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 dark:border-rose-900/60 dark:text-rose-400 dark:hover:border-rose-800 dark:hover:bg-rose-950/30 dark:hover:text-rose-300"
                >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" />
                    Logout
                </Button>
            </div>
        </aside>
    );
};
